"use client"

import OptimizedImage from "@/components/ui/Image/OptimizedImage"
import { UserIcon } from "@heroicons/react/20/solid"
import { useState } from "react"

export type FAQItem = { question: string; answer: string }

export type FAQProps = {
   title?: string
   faqs?: FAQItem[]
}

const RA_ABOUT_US_SRC = "/_logos/logo-restaurant-association-icon-only-color.webp"

const defaultFaqs: FAQItem[] = [
   {
      question: "What is a good labor cost percentage for a restaurant?",
      answer:
         "The ideal range is 28%—35%. However, this varies by format — Quick Service Restaurants (QSRs) often target 25% or below, while full-service restaurants may run up to 35% due to higher staffing needs.",
   },
   {
      question: "What is included in restaurant labor costs?",
      answer:
         "Restaurant labor costs include wages, salaries, overtime pay, payroll taxes, employee benefits (health insurance, paid time off), and any bonuses or tips paid out. Both front-of-house and back-of-house staff should be included in the calculation.",
   },
   {
      question: "How often should I calculate my labor cost?",
      answer:
         "Ideally, calculate your labor cost daily or weekly to catch issues early. Monthly reviews are the minimum for staying on top of trends. Many operators track it daily alongside sales to adjust scheduling in real time.",
   },
   {
      question: "What is the difference between labor cost and prime cost?",
      answer:
         "Labor cost covers only your staffing expenses, while prime cost combines both labor cost and cost of goods sold (COGS). Prime cost is the single most important metric in restaurant finance — most profitable restaurants target a prime cost below 60% of total sales.",
   },
]

const ToggleIcon = ({ open }: { open: boolean }) => (
   <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-brandOrange text-brandOrange">
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
         {open ? <path d="M5 12h14" /> : <path d="M12 5v14M5 12h14" />}
      </svg>
   </div>
)

const FAQItemComponent = ({ question, answer }: FAQItem) => {
   const [open, setOpen] = useState(false)

   return (
      <div className="border-t border-separation-line first:border-t-0">
         <button
            onClick={() => setOpen(!open)}
            className="flex w-full items-center gap-3 px-4 py-4 text-left sm:gap-4 sm:px-6 sm:py-5"
         >
            <div className="flex-shrink-0 rounded-full bg-[#e3f2ff]">
               <UserIcon className="h-8 w-8 text-[#9cd1ff] sm:h-12 sm:w-12" />
            </div>
            <span className="flex-1 text-custom-base-bold text-gray-800 sm:text-custom-xl-bold">{question}</span>
            <ToggleIcon open={open} />
         </button>

         {open && (
            <div className="px-4 pb-4 sm:px-6 sm:pb-5">
               <div className="flex items-start gap-3 rounded-xl bg-orange-50 px-4 py-4 sm:gap-4 sm:px-5">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-brandOrange bg-white sm:h-10 sm:w-10">
                     <OptimizedImage
                        src={RA_ABOUT_US_SRC}
                        width={22}
                        height={22}
                        alt="Industry Growth Chart"
                        className="h-auto w-full rounded-full bg-white p-1 drop-shadow-2xl"
                     />
                  </div>
                  <p className="text-custom-base-normal leading-relaxed text-gray-700">{answer}</p>
               </div>
            </div>
         )}
      </div>
   )
}

const FAQ = ({ title = "Frequently Asked Questions", faqs = defaultFaqs }: FAQProps) => {
   return (
      <div>
         <h2 className="highlight-title mb-6 text-custom-3xl-bold font-bold leading-8">{title}</h2>

         <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {faqs.map((faq) => (
               <FAQItemComponent key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
         </div>
      </div>
   )
}

export default FAQ
